import type { Timestamp } from 'firebase/firestore';

export type DeliveryChannel = 'bell' | 'email' | 'slack_dm' | 'slack_crm';
export type DeliveryStatus = 'sent' | 'skipped' | 'failed';

export interface NotificationDeliveryLogEntry {
  id: string;
  createdAt: Date | null;
  channel: DeliveryChannel;
  status: DeliveryStatus;
  eventType: string;
  eventLabel: string;
  source: string;
  recipientUserId?: string | null;
  recipientName?: string | null;
  recipientEmail?: string | null;
  triggeredByUserId?: string | null;
  triggeredByName?: string | null;
  skipReason?: string | null;
  error?: string | null;
  inAppType?: string | null;
  title?: string | null;
  message?: string | null;
  userNotificationId?: string | null;
  taskId?: string | null;
  projectId?: string | null;
  leadId?: string | null;
  dealId?: string | null;
  opsTaskId?: string | null;
  sourceId?: string | null;
}

export function parseDeliveryLogDoc(
  id: string,
  data: Record<string, unknown>,
): NotificationDeliveryLogEntry {
  const createdAtRaw = data.createdAt as Timestamp | undefined;
  return {
    id,
    createdAt: createdAtRaw?.toDate?.() ?? null,
    channel: (data.channel as DeliveryChannel) || 'bell',
    status: (data.status as DeliveryStatus) || 'sent',
    eventType: String(data.eventType || ''),
    eventLabel: String(data.eventLabel || ''),
    source: String(data.source || ''),
    recipientUserId: (data.recipientUserId as string) || null,
    recipientName: (data.recipientName as string) || null,
    recipientEmail: (data.recipientEmail as string) || null,
    triggeredByUserId: (data.triggeredByUserId as string) || null,
    triggeredByName: (data.triggeredByName as string) || null,
    skipReason: (data.skipReason as string) || null,
    error: (data.error as string) || null,
    inAppType: (data.inAppType as string) || null,
    title: (data.title as string) || null,
    message: (data.message as string) || null,
    userNotificationId: (data.userNotificationId as string) || null,
    taskId: (data.taskId as string) || null,
    projectId: (data.projectId as string) || null,
    leadId: (data.leadId as string) || null,
    dealId: (data.dealId as string) || null,
    opsTaskId: (data.opsTaskId as string) || null,
    sourceId: (data.sourceId as string) || null,
  };
}

export function formatDeliveryChannel(channel: DeliveryChannel): string {
  switch (channel) {
    case 'bell':
      return 'bell';
    case 'email':
      return 'email';
    case 'slack_dm':
      return 'slack:dm';
    case 'slack_crm':
      return 'slack:#crm';
    default:
      return channel;
  }
}

export function formatDeliveryStatus(entry: NotificationDeliveryLogEntry): string {
  if (entry.status === 'sent') return 'sent';
  if (entry.status === 'failed') return entry.error ? `failed (${entry.error})` : 'failed';
  return entry.skipReason ? `skipped (${entry.skipReason})` : 'skipped';
}
